import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { useToast } from "@packages/contexts/ToastContext";
import {
  useGetSlideGenerators,
  useDeleteSlideGenerator,
  getGetSlideGeneratorsQueryKey,
} from "@packages/api/madlenAPI";
import { AlertTriangle, Package } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import HeroTitle from "../../../tools/components/tool_components/HeroTitle";
import DeleteConfirmPopUp from "../../../tools/components/tool_components/DeleteConfirmPopUp";

import { trackEvent } from "@packages/hooks/useAnalytics";
import schemas from "../../components/schemas";
import Modal from "@core/modal";
import Card from "../../../tools/components/card";
import { Text, Button } from "@core/index";

const SlidesView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [isSlidesBetaSeen, setIsSlidesBetaSeen] = useState(
    localStorage.getItem("slides-beta-seen") === "true",
  );
  const { data: slideGenerators, isLoading, error } = useGetSlideGenerators();

  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const queryClient = useQueryClient();

  const deleteSlideGenerator = useDeleteSlideGenerator({
    mutation: {
      onMutate: async ({ id }) => {
        const getSlideGeneratorsQueryKey = getGetSlideGeneratorsQueryKey();
        const previousData = queryClient.getQueryData(
          getSlideGeneratorsQueryKey,
        );

        // Optimistically update UI
        queryClient.setQueryData(getSlideGeneratorsQueryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter((slide: any) => slide.id !== id),
          };
        });

        return { previousData };
      },
      onError: (error, _variables, context) => {
        const getSlideGeneratorsQueryKey = getGetSlideGeneratorsQueryKey();
        if (context?.previousData) {
          queryClient.setQueryData(
            getSlideGeneratorsQueryKey,
            context.previousData,
          );
        }
        console.error("Failed to delete slide:", error);
      },
      onSettled: () => {
        const getSlideGeneratorsQueryKey = getGetSlideGeneratorsQueryKey();
        queryClient.invalidateQueries({ queryKey: getSlideGeneratorsQueryKey });
      },
    },
  });

  const handleDelete = async (slideId: number) => {
    try {
      await deleteSlideGenerator.mutateAsync({ id: slideId });
      addToast({
        title: t("slides.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t("slides.deleteError"),
        description: t("slides.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleClickDescription = () => {
    track(
      {
        category: "ContentDescription",
        action: "slide.description",
        label: "Slide description click",
        nonInteraction: false,
      },
      {
        location: "/slide/description",
        element: "description-button",
      },
    );
    navigate("/slides/create");
  };

  const handleClickGeneration = () => {
    track(
      {
        category: "ContentGeneration",
        action: "slide.generate",
        label: "Slide generation click",
        nonInteraction: false,
      },
      {
        location: "/slide/generate",
        element: "generate-button",
      },
    );
  };

  // Helper function to get the first slide preview
  const getFirstSlidePreview = (slideGenerator: any) => {
    if (
      !slideGenerator?.presentation?.slides ||
      slideGenerator.presentation.slides.length === 0
    ) {
      return null;
    }

    const firstSlideConfig = slideGenerator.presentation.slides[0];
    const schemaId = Object.keys(firstSlideConfig)[0];
    const schema = schemas.find((s: any) => s.id === schemaId);

    if (!schema) return null;

    return (
      <div className="w-full h-full">
        {schema.render(firstSlideConfig[schemaId])}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-rose-100 dark:from-gray-900 dark:to-rose-900">
      <div className="container max-w-[1920px] mx-auto px-4 py-8">
        <HeroTitle
          onClick={handleClickGeneration}
          title={t("slides.title")}
          description={t("slides.description")}
          titleStyle="text-4xl md:text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-rose-600 via-pink-600 to-red-600 dark:from-rose-400 dark:via-pink-400 dark:to-red-400 py-3"
          titleBackgroundStyle="bg-gradient-to-r from-rose-50/30 to-pink-50/30 dark:from-rose-900/30 dark:to-pink-900/30 blur-xl"
          buttonStyle="relative group inline-flex items-center justify-center px-6 py-3 text-sm font-medium transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white rounded-full shadow-lg hover:shadow-xl"
          buttonLink="/slides/create"
        />

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {t("slides.loading")}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-red-50 dark:bg-red-900/20 rounded-2xl p-8">
            <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/50">
              <AlertTriangle
                size={32}
                className="text-red-600 dark:text-red-400"
              />
            </div>
            <Text variant={"h2"} className="!text-red-600 dark:!text-red-400">
              slides.error.title
            </Text>
            <p className="text-gray-600 dark:text-gray-300 text-center max-w-md">
              {t("slides.error.loadingError")}
            </p>

            <Button
              bgColor="bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900/70"
              rounded="lg"
              textColor="text-red-600 dark:text-red-400"
              onClick={() => window.location.reload()}
              size="md"
            >
              {t("slides.error.retryButton")}
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !error &&
          (!slideGenerators || slideGenerators.length === 0) && (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-gray-50 dark:bg-gray-900/20 rounded-2xl p-8">
              <div className="p-4 rounded-full bg-rose-100 dark:bg-rose-800">
                <Package
                  size={32}
                  className="text-rose-600 dark:text-rose-400"
                />
              </div>
              <Text variant={"h2"}>slides.empty.title</Text>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                {t("slides.empty.description")}
              </p>
              <Button
                onClick={handleClickDescription}
                bgColor="bg-rose-500 hover:bg-rose-600"
                rounded="lg"
                textColor="text-white"
                size="md"
              >
                {t("slides.empty.createButton")}
              </Button>
            </div>
          )}

        {/* Slides Grid - Add this when you have slides to display */}
        {!isLoading &&
          !error &&
          slideGenerators &&
          slideGenerators.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6">
              {slideGenerators.map((slideGenerator) => (
                <Card
                  key={slideGenerator.id}
                  item={{
                    id: slideGenerator.id.toString(),
                    imageUrl: "",
                    lessonTopic: slideGenerator.topic || "",
                    courseName: slideGenerator.courseType,
                    gradeLevel: slideGenerator.grade,
                    description: "",
                  }}
                  selectItem={() => {
                    navigate(`/slides/${slideGenerator.id}`);
                  }}
                  setShowDeletePopup={(id) => setShowDeletePopup(Number(id))}
                  customPreview={
                    <div className="w-full aspect-[16/9] rounded-t-lg overflow-hidden relative bg-white dark:bg-gray-800">
                      <div
                        className="absolute inset-0 w-full h-full"
                        style={{
                          transform: "scale(0.25)",
                          transformOrigin: "top left",
                        }}
                      >
                        <div style={{ width: "400%", height: "400%" }}>
                          {getFirstSlidePreview(slideGenerator)}
                        </div>
                      </div>
                    </div>
                  }
                  className="h-full"
                />
              ))}
            </div>
          )}

        {!isSlidesBetaSeen && (
          <Modal
            onClose={() => {
              localStorage.setItem("slides-beta-seen", "true");
              setIsSlidesBetaSeen(true);
            }}
            show={true}
            title={t("slides.beta.title")}
            actions={[
              {
                label: t("slides.beta.understood"),
                onClick: () => {
                  localStorage.setItem("slides-beta-seen", "true");
                  setIsSlidesBetaSeen(true);
                },
                variant: "primary",
              },
            ]}
          >
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t("slides.beta.description")}
            </p>
          </Modal>
        )}
        {/* Delete Confirmation Popup */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("slides.confirmDelete", {
              title:
                slideGenerators &&
                slideGenerators.find((slide) => slide.id === showDeletePopup)
                  ?.name,
            })}
            showDeletePopup={showDeletePopup}
            handleDelete={handleDelete}
            setShowDeletePopup={setShowDeletePopup}
          />
        )}
      </div>
    </div>
  );
};

export default SlidesView;
